import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Link,
  Button
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, InsertDriveFile as FileIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

const TimelineView = ({ events, onEdit, onDelete }) => {
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return dayjs(date).format('MMM DD, YYYY');
  };

  const formatYear = (date) => {
    if (!date) return 'Unknown';
    return dayjs(date).format('YYYY');
  };

  // Group events by year
  const eventsByYear = events.reduce((acc, event) => {
    const year = formatYear(event.eventDate);
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(event);
    return acc;
  }, {});

  // Sort years descending
  const years = Object.keys(eventsByYear).sort((a, b) => b.localeCompare(a));

  if (events.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No events found. Add your first event to get started!
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 2 }}>
      {years.map((year, yearIndex) => (
        <Box key={year} sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>
            {year}
          </Typography>
          <Box sx={{ position: 'relative', pl: 4 }}>
            {/* Timeline line */}
            {yearIndex < years.length - 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 15,
                  top: 0,
                  bottom: -32,
                  width: 2,
                  bgcolor: 'divider'
                }}
              />
            )}
            {eventsByYear[year].map((event, eventIndex) => (
              <Box key={event.id} sx={{ mb: 3, position: 'relative' }}>
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: -29,
                    top: 8,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: event.category === 'work' ? 'primary.main' : 'secondary.main',
                    border: 2,
                    borderColor: 'background.paper',
                    zIndex: 1
                  }}
                />
                <Card elevation={2}>
                  {(event.documentUrl || event.imageUrl) && (event.documentType === 'image' || (!event.documentType && !(event.documentUrl || event.imageUrl)?.endsWith('.pdf'))) && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={event.documentUrl || event.imageUrl}
                      alt={event.title}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  {(event.documentUrl || event.imageUrl) && (event.documentType === 'pdf' || (event.documentUrl || event.imageUrl)?.endsWith('.pdf') || (event.documentUrl || event.imageUrl)?.includes('.pdf')) && (
                    <Box sx={{ p: 2, bgcolor: 'grey.100', textAlign: 'center' }}>
                      <FileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {event.fileName || 'PDF Document'}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<OpenIcon />}
                        href={event.documentUrl || event.imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open PDF
                      </Button>
                    </Box>
                  )}
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h3" gutterBottom>
                          {event.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {formatDate(event.eventDate)}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(event)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(event.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Chip
                      label={event.category || 'N/A'}
                      size="small"
                      color={event.category === 'work' ? 'primary' : 'secondary'}
                      sx={{ mb: 1 }}
                    />
                    {event.location && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        📍 {event.location}
                      </Typography>
                    )}
                    {event.description && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {event.description}
                        </Typography>
                      </>
                    )}
                    {event.tags && event.tags.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                        {event.tags.map((tag) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                      </Box>
                    )}
                    {(event.documentUrl || event.imageUrl) && event.documentType !== 'pdf' && (
                      <Box sx={{ mt: 1 }}>
                        <Link
                          href={event.documentUrl || event.imageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                        >
                          View Document
                        </Link>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default TimelineView;

